import { type ReactElement } from "react";

import type { ParticipantProfile, ResolvedProtocol } from "../../api/types";
import { ConsentFlow } from "./ConsentFlow";
import { OneTimeQuestionnaire } from "./OneTimeQuestionnaire";

// One-time enrollment gate shown to participants who haven't finished setup:
// step 1 consent → step 2 initial questionnaire. Reuses the existing flow
// components. Consent activates enrollment (reloadProfile unlocks step 2); the
// questionnaire's onSubmitted lets the parent re-check and flip to the dashboard.
export function Onboarding({
  profile,
  protocol,
  reloadProfile,
  reloadQuestionnaires,
}: {
  profile: ParticipantProfile | null;
  protocol: ResolvedProtocol | null;
  reloadProfile: () => Promise<void>;
  reloadQuestionnaires: () => Promise<void>;
}): ReactElement {
  const active = profile?.enrollment_status === "active";

  return (
    <>
      <div className="onboarding-intro">
        <h1>Welcome to FaceAge</h1>
        <p className="muted">
          Two quick steps to finish setting up your account. Once they're done, your
          dashboard opens up.
        </p>
      </div>

      <section className="app-card">
        <h2>
          <span className="step-pill">1</span>Consent
        </h2>
        <ConsentFlow profile={profile} protocol={protocol} onChange={reloadProfile} />
      </section>

      <section className={`app-card${active ? "" : " locked"}`}>
        <h2>
          <span className="step-pill">2</span>Initial questionnaire
        </h2>
        {active ? (
          // Reload the PROFILE too: the submit mirrors `sex` onto
          // participants.sex_at_birth server-side, and with
          // refetchOnWindowFocus off a stale cached profile would show
          // "Sex at birth: Not provided" for the whole session (participant
          // report, 2026-09-01).
          <OneTimeQuestionnaire
            active
            onSubmitted={() => void Promise.all([reloadProfile(), reloadQuestionnaires()])}
          />
        ) : (
          <p className="muted">Complete consent above to unlock the questionnaire.</p>
        )}
      </section>
    </>
  );
}
